import {
  apiVersion,
  dataset,
  projectId,
  studioUrl,
  useCdn,
} from 'lib/sanity.api';
import {
  indexQuery,
  type Post,
  postAndMoreStoriesQuery,
  postBySlugQuery,
  postSlugsQuery,
  type Settings,
  settingsQuery,
} from 'lib/sanity.queries';
import { createClient, type SanityClient } from 'next-sanity';

/**
 * Returns a configured Sanity client instance.
 * If a preview token is provided, the client will be configured for draft previews.
 * If `write` is set to true, the client will include the write token for authenticated requests.
 *
 * @param preview - Optional configuration for preview mode, including token and write flag.
 * @returns A configured Sanity client instance.
 */
export function getClient(preview?: {
  token?: string;
  write?: boolean;
}): SanityClient {
  const client = createClient({
    projectId,
    dataset,
    apiVersion,
    useCdn,
    token:
      preview?.token ||
      (preview?.write ? process.env.SANITY_API_WRITE_TOKEN : undefined),
    perspective: preview?.write ? 'published' : 'published',
    stega: {
      enabled: preview?.token ? true : false,
      studioUrl,
    },
  });

  if (preview && preview.token) {
    return client.withConfig({
      token: preview.token,
      useCdn: false,
      ignoreBrowserTokenWarning: true,
      perspective: 'previewDrafts',
    });
  }

  return client;
}

export const getSanityImageConfig = () => getClient();

/**
 * Fetches settings data from Sanity.
 *
 * @param client - The Sanity client instance.
 * @returns A promise that resolves to the Settings data.
 */
export async function getSettings(client: SanityClient): Promise<Settings> {
  return (await client.fetch(settingsQuery)) || {};
}

/**
 * Fetches all posts from Sanity.
 *
 * @param client - The Sanity client instance.
 * @returns A promise that resolves to an array of Post objects.
 */
export async function getAllPosts(client: SanityClient): Promise<Post[]> {
  return (await client.fetch(indexQuery)) || [];
}

/**
 * Fetches all post slugs from Sanity.
 *
 * @returns A promise that resolves to an array of slugs.
 */
export async function getAllPostsSlugs(): Promise<Pick<Post, 'slug'>[]> {
  const client = getClient();
  const slugs = (await client.fetch<string[]>(postSlugsQuery)) || [];
  return slugs.map((slug) => ({ slug }));
}

/**
 * Fetches a post by its slug.
 *
 * @param client - The Sanity client instance.
 * @param slug - The slug of the post to fetch.
 * @returns A promise that resolves to a Post object.
 */
export async function getPostBySlug(
  client: SanityClient,
  slug: string
): Promise<Post> {
  return (await client.fetch(postBySlugQuery, { slug })) || ({} as any);
}

/**
 * Fetches a post and more stories by its slug.
 *
 * @param client - The Sanity client instance.
 * @param slug - The slug of the post.
 * @returns A promise that resolves to an object containing the post and more posts.
 */
export async function getPostAndMoreStories(
  client: SanityClient,
  slug: string
): Promise<{ post: Post; morePosts: Post[] }> {
  return await client.fetch(postAndMoreStoriesQuery, { slug });
}

/**
 * Fetches all repositories from Sanity.
 *
 * @param client - The Sanity client instance.
 * @returns A promise that resolves to an array of repositories.
 */
export async function getAllRepositories(client: SanityClient): Promise<any[]> {
  const query = `*[_type == "repository"] | order(createdAt desc) {
    _id,
    name,
    description,
    url,
    stargazerCount,
    forkCount,
    languages,
    createdAt,
    updatedAt
  }`;

  return (await client.fetch(query)) || [];
}

/**
 * Fetches narratives from Sanity.
 *
 * @param client - The Sanity client instance.
 * @returns A promise that resolves to narrative data.
 */
export async function getNarratives(client: SanityClient): Promise<any[]> {
  const query = `*[_type == "githubProject"] | order(lastUpdated desc) {
    _id,
    narrativeTitle,
    narrativeDescription,
    relatedRepositories[]->{_id, name},
    tags,
    lastUpdated
  }`;

  return (await client.fetch(query)) || [];
}

/**
 * Fetches enriched graph data from Sanity.
 *
 * @param client - The Sanity client instance.
 * @returns A promise that resolves to enriched graph data.
 */
export async function getEnrichedGraphData(
  client: SanityClient
): Promise<any> {
  const query = `*[_type == "graphMetadata"]{
    totalNodes,
    totalLinks,
    graphData
  }[0]`;

  return (await client.fetch(query)) || null;
}